import { ClientPostTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';

import { EMPTY_POLL_CREATE } from '@/features/post-editor/constants/poll';
import { EMPTY_POST_FORM } from '@/features/post-editor/constants/postForm';
import type { PollCreate } from '@/features/post-editor/types/poll';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { convertPollCreateToClientPoll } from '@/features/post-editor/utils/poll';
import { PostFeedType } from '@/features/posts/types/postFeed';
import type { GenericPostDetail } from '@/features/posts/types/posts';
import {
  POST_TYPE_POST_FEED_TYPE,
  POST_FEED_TYPE_POST_TYPE
} from '@/features/posts/utils/postFeed';

type PostToFormOptions = {
  post: GenericPostDetail;
};

/**
 * Maps a `GenericPostDetail` (from `useGetPostById`) to the form's initial
 * values. Channel/sub-type are derived from `post.postType`; the form keeps the
 * rest of its shape so `PostForm` works the same way in create and edit.
 *
 * Today the API embeds the full `suggestionCategory` on the post, so we read
 * it directly. If a future API version returns only the id, pass the loaded
 * category list as a second argument and resolve from there.
 */
export const postToFormValues = ({
  post
}: PostToFormOptions): PostFormValues => {
  const channelType =
    POST_TYPE_POST_FEED_TYPE[post.postType ?? ClientPostTypeEnum.Gossip] ??
    PostFeedType.GOSSIP;
  const marketType =
    channelType === PostFeedType.MARKET
      ? (post.postType ?? POST_FEED_TYPE_POST_TYPE[PostFeedType.MARKET])
      : EMPTY_POST_FORM.marketType;
  const dorfHelferType = post.dorfHelferType ?? EMPTY_POST_FORM.dorfHelferType;

  const startDate = post.startTime ? new Date(post.startTime) : undefined;
  const endDate = post.endTime ? new Date(post.endTime) : undefined;

  const pollCreateRequest: PollCreate | undefined = post.poll
    ? {
        text: post.poll.text ?? '',
        multipleChoice: post.poll.multipleChoice ?? false,
        // TODO(product): confirm default re-open duration for expired polls.
        // A poll whose stored expiration is missing or already in the past
        // would be re-created already-expired, so fall back to the default
        // future duration.
        expirationTime: resolvePollExpiration(post.poll.expirationTime),
        optionTexts: extractPollOptionTexts(post.poll)
      }
    : undefined;

  return {
    ...EMPTY_POST_FORM,
    channelType,
    marketType,
    dorfHelferType,
    title: post.title ?? '',
    text: post.text ?? '',
    allDay: post.allDayEvent ?? EMPTY_POST_FORM.allDay,
    startDate,
    endDate,
    location: post.customAddress ?? undefined,
    suggestionCategory: post.suggestionCategory ?? undefined,
    poll: pollCreateRequest
      ? convertPollCreateToClientPoll(pollCreateRequest)
      : undefined,
    hiddenForOtherHomeAreas:
      post.hiddenForOtherHomeAreas ?? EMPTY_POST_FORM.hiddenForOtherHomeAreas,
    geoAreaIds: post.geoAreaIds ?? undefined,
    categories: post.categories ?? undefined
  };
};

const extractPollOptionTexts = (
  poll: NonNullable<GenericPostDetail['poll']>
): PollCreate['optionTexts'] => {
  const options: PollCreate['optionTexts'] = [];
  for (let i = 1; i <= 5; i++) {
    const key = `optionText${i}` as keyof typeof poll;
    const value = poll[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      options.push({ optionText: value.trim() });
    }
  }
  while (options.length < 2) {
    options.push({ optionText: '' });
  }
  return options;
};

const resolvePollExpiration = (expirationTime?: number): number => {
  if (typeof expirationTime === 'number' && expirationTime > Date.now()) {
    return expirationTime;
  }
  return EMPTY_POLL_CREATE.expirationTime;
};
